import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnChanges, OnInit, Signal, SimpleChanges, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PremiumResidence } from '@lx/shared/data-access/products';
import { Amount } from '@lx/shared/data-access/types';
import { ButtonComponent } from '@lx/shared/ui/button';
import { LoadingService } from '@lx/shared/ui/loading-spinner';
import { LocalizeAndPluralizePeriodPipe } from '@lx/shared/ui/pipes';
import { BehaviorSubject, Observable } from 'rxjs';
import { GoogleAnalyticsEventEnum } from '../../google-analytics';
import { QTLiteStoreSignalsAdapterService } from '../../services/qt-lite-store-signals-adapter.service';
import { QuoteOutputProduct, QuoteStore, QuoteToolLiteStore } from '../../store';
import { PREMIUM_ACCOMMODATIONS_FALLBACK_IMAGE } from '../../types';
import { CardContainerComponent, ImageComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { OptionCardComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { SidePanelGalleryComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { OperationKey } from '../../constants';
import { SidePanelService } from '@lx/quote-tool/lite/shared/services';

@Component({
  standalone: true,
  selector: 'lx-premium-accommodation',
  templateUrl: './premium-accommodation.component.html',
  styleUrls: ['./premium-accommodation.component.scss'],
  imports: [
    CommonModule,
    CardContainerComponent,
    OptionCardComponent,
    ButtonComponent,
    SidePanelGalleryComponent,
    LocalizeAndPluralizePeriodPipe,
    ImageComponent,
  ],
})
export class PremiumAccommodationComponent implements OnInit, OnChanges {
  @Input() premiumAccommodation: PremiumResidence | undefined;

  selected = false;
  price: Amount[] | undefined;
  endPrice: Amount[] | undefined;
  discount: string | undefined;
  duration: number | undefined;
  selectedPremiumAccommodationCode = '';
  premiumAccommodationDialogVisible = false;
  currency: Signal<string | undefined>;

  dataLayerEventName$: Observable<GoogleAnalyticsEventEnum> | undefined;

  protected readonly PREMIUM_ACCOMMODATIONS_FALLBACK_IMAGE = PREMIUM_ACCOMMODATIONS_FALLBACK_IMAGE;

  protected loading: Signal<boolean> = signal(false);

  private readonly dataLayerEventName = new BehaviorSubject<GoogleAnalyticsEventEnum>(undefined);

  private readonly destroyRef = inject(DestroyRef);
  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly qtLiteStoreSignalsAdapterService = inject(QTLiteStoreSignalsAdapterService);
  private readonly quoteStore = inject(QuoteStore);
  private readonly sidePanelService = inject(SidePanelService);
  private readonly loadingService = inject(LoadingService);

  ngOnInit(): void {
    this.quoteToolLiteStore.selectedDuration$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((duration) => (this.duration = duration));
    this.quoteStore.premiumResidence$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((quotedInsurance: QuoteOutputProduct) => {
      this.setPropertiesFromQuotedInsurance(quotedInsurance);
    });
    this.quoteToolLiteStore.selectedPremiumAccommodation$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((selectedPremiumAccommodationCode) => {
      this.selected = !!selectedPremiumAccommodationCode;
    });
    this.dataLayerEventName$ = this.dataLayerEventName.asObservable();
    this.currency = this.qtLiteStoreSignalsAdapterService.selectedCurrency;
    this.loading = this.loadingService.areAllOperationsLoading([OperationKey.prices, OperationKey.extras]);
  }

  ngOnChanges({ premiumAccommodation }: SimpleChanges): void {
    if (premiumAccommodation.currentValue) {
      const totalPriceFromExtrasInsurance = this.calculateEndPriceForDuration(premiumAccommodation.currentValue?.multiCurrencyPrice, this.duration);
      this.endPrice = totalPriceFromExtrasInsurance;
      this.price = totalPriceFromExtrasInsurance;
    }
  }

  addOrRemovePremiumAccommodation(selectedPremiumAccommodationCode: string) {
    if (!this.selectedPremiumAccommodationCode) {
      this.quoteToolLiteStore.updateState({ selectedPremiumAccommodationCode });
      this.dataLayerEventName.next(GoogleAnalyticsEventEnum.QuotePremiumResidenceAdd);
    } else {
      this.quoteToolLiteStore.updateState({ selectedPremiumAccommodationCode: undefined });
      this.dataLayerEventName.next(GoogleAnalyticsEventEnum.QuotePremiumResidenceRemove);
    }
  }

  addOrRemoveCode(): void {
    this.selected = !this.selected;
    const selectedPremiumAccommodationCode = this.selected ? this.premiumAccommodation?.code : undefined;
    this.quoteToolLiteStore.updateState({ selectedPremiumAccommodationCode });
  }

  onLearnMoreClick() {
    this.premiumAccommodationDialogVisible = true;
  }

  hidePremiumAccommodationDialog() {
    this.sidePanelService.hidePanel(() => (this.premiumAccommodationDialogVisible = false));
  }

  private setPropertiesFromQuotedInsurance(quotedPremiumAccommodation: QuoteOutputProduct): void {
    this.selectedPremiumAccommodationCode = quotedPremiumAccommodation?.code;
    this.endPrice = quotedPremiumAccommodation?.endPrice || this.calculateEndPriceForDuration(this.premiumAccommodation?.multiCurrencyPrice, this.duration);
    this.price = quotedPremiumAccommodation?.price || this.endPrice;
    this.discount = quotedPremiumAccommodation?.discountTotalAsPercentage;
  }

  private calculateEndPriceForDuration(multiCurrencyPrice: Amount[], duration: number): Amount[] {
    return multiCurrencyPrice?.map((price) => ({
      ...price,
      value: price.value * duration,
    }));
  }
}
